/**
 * Form Validation Schemas
 * Zod schemas for slip verification and manual entry forms
 */

import { z } from "zod"

/**
 * Verify Slip Form Schema
 * For automatic slip verification
 */
export const verifySlipSchema = z.object({
  branch_id: z.string().min(1, "กรุณาเลือกสาขา"),
  image: z
    .instanceof(File, { message: "กรุณาเลือกไฟล์รูปสลิป" })
    .refine((file) => file.size <= 20 * 1024 * 1024, "ขนาดไฟล์ต้องไม่เกิน 20MB")
    .refine(
      (file) => ["image/jpeg", "image/jpg", "image/png"].includes(file.type),
      "รองรับเฉพาะไฟล์ .jpg, .jpeg, .png เท่านั้น"
    ),
})

export type VerifySlipFormData = z.infer<typeof verifySlipSchema>

/**
 * Manual Slip Form Schema
 * For manual slip entry without bank verification
 */
export const manualSlipSchema = z.object({
  account_id: z.string().min(1, "กรุณาเลือกบัญชี"),
  bank_code: z.string().min(1, "กรุณาเลือกธนาคารผู้โอน"),
  sender_name: z.string().min(1, "กรุณากรอกชื่อผู้โอน").max(255, "ชื่อผู้โอนยาวเกินไป"),
  amount: z
    .number({
      required_error: "กรุณากรอกจำนวนเงิน",
      invalid_type_error: "จำนวนเงินต้องเป็นตัวเลข",
    })
    .positive("จำนวนเงินต้องมากกว่า 0")
    .max(999999999, "จำนวนเงินเกินกำหนด"),
  transfer_date: z.string().min(1, "กรุณาเลือกวันเวลาตามสลิป"),
  status: z.enum(["success", "pending", "failed"], {
    required_error: "กรุณาเลือกสถานะ",
  }),
  image: z
    .instanceof(File)
    .refine((file) => file.size <= 20 * 1024 * 1024, "ขนาดไฟล์ต้องไม่เกิน 20MB")
    .refine(
      (file) => ["image/jpeg", "image/jpg", "image/png"].includes(file.type),
      "รองรับเฉพาะไฟล์ .jpg, .jpeg, .png เท่านั้น"
    )
    .optional(),
})

export type ManualSlipFormData = z.infer<typeof manualSlipSchema>

/**
 * Helper function to format amount input
 */
export function parseAmountInput(value: string): number {
  // Remove commas and convert to number
  const cleaned = value.replace(/,/g, "")
  const num = parseFloat(cleaned)
  return isNaN(num) ? 0 : num
}

/**
 * Helper function to format amount for display
 */
export function formatAmountInput(value: number): string {
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}
